// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/solid.tsx
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/shortcuts/src/solid.tsx
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo stockpilot-app
//     node fleet/fleet.ts check --repo stockpilot-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Solid bindings for the shortcut system.
 *
 *   <ShortcutsProvider> mounts the engine and exposes the registry via context.
 *   useCommand(...)      registers a command and (optionally) a handler.
 *   useBinding(...)      adds an extra key binding (rarely needed — defaults cover most).
 *   useScope(id, when?)  pushes a scope while the component is mounted.
 *   useWhenContext({...})sets reactive WhenContext flags.
 *   useShortcuts()       gives access to dispatch / registry / formatter.
 */

import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  onCleanup,
  onMount,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { createMutable } from "solid-js/store";

import { createEngine, type Engine } from "./engine";
import { flatFormatBinding, formatBinding } from "./format";
import { localStoragePersistence } from "./persistence";
import { createPlatformAdapter, detectPlatform } from "./platform";
import { createRegistry, type Registry } from "./registry";
import type {
  Command,
  CommandHandler,
  KeyBinding,
  PersistenceAdapter,
  Platform,
  PlatformAdapter,
  ScopeId,
  WhenContext,
} from "./types";

interface ShortcutsContextValue {
  registry: Registry;
  engine: Engine;
  platform: PlatformAdapter;
  context: WhenContext;
  dispatch: (commandId: string, event?: KeyboardEvent) => void;
  format: (spec: string) => ReturnType<typeof formatBinding>;
  formatFlat: (spec: string) => string[];
}

const ShortcutsContext = createContext<ShortcutsContextValue | undefined>(undefined);

interface ShortcutsProviderProps {
  initialCommands?: readonly Command[];
  initialBindings?: readonly KeyBinding[];
  persistence?: PersistenceAdapter;
  platform?: Platform | PlatformAdapter;
  chordTimeoutMs?: number;
  /**
   * Shared interaction context. When supplied (by `<InteractionProvider>`), the
   * engine reads when-clause flags from it and the provider that owns it is
   * responsible for focus tracking. When omitted the engine creates and tracks
   * its own store (standalone / test usage).
   */
  context?: WhenContext;
}

function isPlatformAdapter(value: unknown): value is PlatformAdapter {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const id: unknown = Reflect.get(value, "id");
  const normalizeEvent: unknown = Reflect.get(value, "normalizeEvent");
  return typeof id === "string" && typeof normalizeEvent === "function";
}

export const ShortcutsProvider: ParentComponent<ShortcutsProviderProps> = (props) => {
  // The provider's props are configuration, evaluated once at setup; we read
  // them via untrack() to make this intent explicit and quiet solid/reactivity.
  const platformAdapter: PlatformAdapter = untrack(() => {
    const raw = props.platform;
    if (isPlatformAdapter(raw)) {
      return raw;
    }
    return createPlatformAdapter(typeof raw === "string" ? raw : detectPlatform());
  });

  const registry = createRegistry(platformAdapter.id);
  // Prefer a shared interaction context when one is provided; otherwise own a
  // private store so the provider keeps working standalone (e.g. in tests).
  const externalContext = untrack(() => props.context);
  const contextStore =
    externalContext ??
    createMutable<WhenContext>({
      "input.focused": false,
      "input.text-cursor": false,
      "dialog.open": false,
      "palette.open": false,
      "drawer.open": false,
      "todo.focused": false,
      "selection.active": false,
    });

  // Seed registry with initial commands and bindings.
  const initialCommands = untrack(() => props.initialCommands);
  if (initialCommands) {
    for (const cmd of initialCommands) {
      registry.registerCommand(cmd);
    }
  }
  const initialBindings = untrack(() => props.initialBindings);
  if (initialBindings) {
    registry.registerBindings(initialBindings);
  }

  // Load user overrides on mount.
  const persistence = untrack(() => props.persistence) ?? localStoragePersistence;
  onMount(() => {
    void (async () => {
      try {
        const overrides = await persistence.load();
        registry.setUserOverrides(overrides);
      } catch (err) {
        if (typeof console !== "undefined") {
          console.warn("[shortcuts] failed to load overrides", err);
        }
      }
    })();
  });

  // Persist whenever the override list changes.
  createEffect(() => {
    const overrides = registry.userOverrides();
    void (async () => {
      try {
        await persistence.save(overrides);
      } catch (err) {
        if (typeof console !== "undefined") {
          console.warn("[shortcuts] failed to persist overrides", err);
        }
      }
    })();
  });

  // Track focus state into the context store only when we own it. When an
  // external interaction context is provided, that provider tracks focus.
  // contextStore is a createMutable proxy; direct assignment IS the API.
  onMount(() => {
    if (externalContext) {
      return;
    }
    const updateInputFocused = () => {
      const el = document.activeElement;
      const isInput =
        el instanceof HTMLElement &&
        (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);
      contextStore["input.focused"] = isInput;
      contextStore["input.text-cursor"] = isInput && el.tagName !== "SELECT";
    };
    document.addEventListener("focusin", updateInputFocused, { capture: true });
    document.addEventListener("focusout", updateInputFocused, { capture: true });
    updateInputFocused();
    onCleanup(() => {
      document.removeEventListener("focusin", updateInputFocused, { capture: true });
      document.removeEventListener("focusout", updateInputFocused, { capture: true });
    });
  });

  const engine = createEngine({
    registry,
    platform: platformAdapter,
    contextStore,
    chordTimeoutMs: untrack(() => props.chordTimeoutMs),
  });

  onMount(() => {
    engine.start();
  });
  onCleanup(() => {
    engine.stop();
  });

  const value: ShortcutsContextValue = {
    registry,
    engine,
    platform: platformAdapter,
    context: contextStore,
    dispatch: (commandId, event) => {
      engine.dispatch(commandId, event);
    },
    format: (spec) => formatBinding(spec, platformAdapter),
    formatFlat: (spec) => flatFormatBinding(spec, platformAdapter),
  };

  return <ShortcutsContext.Provider value={value}>{props.children}</ShortcutsContext.Provider>;
};

export function useShortcuts(): ShortcutsContextValue {
  const ctx = useContext(ShortcutsContext);
  if (!ctx) {
    throw new Error("useShortcuts must be used within a <ShortcutsProvider>");
  }
  return ctx;
}

/**
 * Register a command. If the command id is already in the registry (e.g. seeded
 * by defaults), this call updates the handler for the lifetime of the component.
 *
 * @public
 */
export function useCommand(spec: Command): void {
  const { registry } = useShortcuts();
  const existing = registry.getCommand(spec.id);
  if (!existing) {
    const dispose = registry.registerCommand(spec);
    onCleanup(dispose);
    return;
  }
  if (spec.run) {
    const dispose = registry.setHandler(spec.id, spec.run);
    onCleanup(dispose);
  }
}

/**
 * Bind a handler to a known command id. Use this when the command is declared
 * in defaults (so its title/category/binding are already known) but the runtime
 * handler is owned by a specific component.
 */
export function useCommandHandler(commandId: string, handler: CommandHandler): void {
  const { registry } = useShortcuts();
  const dispose = registry.setHandler(commandId, handler);
  onCleanup(dispose);
}

export function useBinding(binding: KeyBinding | (() => KeyBinding)): void {
  const { registry } = useShortcuts();
  const resolved = typeof binding === "function" ? binding() : binding;
  const dispose = registry.registerBinding(resolved);
  onCleanup(dispose);
}

/**
 * Push a scope on the engine's stack for the lifetime of the component (or
 * while the `when` accessor returns true, if provided).
 */
export function useScope(id: ScopeId, when?: Accessor<boolean>): void {
  const { engine } = useShortcuts();
  if (!when) {
    const dispose = engine.pushScope(id);
    onCleanup(dispose);
    return;
  }
  let dispose: (() => void) | undefined = undefined;
  createEffect(() => {
    if (when()) {
      dispose ??= engine.pushScope(id);
    } else if (dispose) {
      dispose();
      dispose = undefined;
    }
  });
  onCleanup(() => {
    dispose?.();
    dispose = undefined;
  });
}

/**
 * Multiple components may push the same flag (e.g. two dialogs both raise
 * `dialog.open`). We refcount contributions per flag globally; the engine
 * context flag is true iff at least one contributor is currently true.
 */
const flagContributors = new Map<string, Set<symbol>>();

function writeFlagContribution(engine: Engine, flag: string, token: symbol, value: boolean): void {
  let set = flagContributors.get(flag);
  if (!set) {
    set = new Set();
    flagContributors.set(flag, set);
  }
  if (value) {
    set.add(token);
  } else {
    set.delete(token);
  }
  engine.setContext(flag, set.size > 0);
}

export function useWhenContext(flags: Record<string, Accessor<boolean>>): void {
  const { engine } = useShortcuts();
  const tokens = new Map<string, symbol>();
  for (const flag of Object.keys(flags)) {
    tokens.set(flag, Symbol(`when:${flag}`));
  }
  for (const [flag, accessor] of Object.entries(flags)) {
    const token = tokens.get(flag);
    if (!token) {
      continue;
    }
    createEffect(() => {
      writeFlagContribution(engine, flag, token, accessor());
    });
  }
  onCleanup(() => {
    for (const [flag, token] of tokens) {
      writeFlagContribution(engine, flag, token, false);
    }
  });
}

/** Returns a (reactive) accessor that lists all commands grouped by category. */
export function useCommandCatalog(): Accessor<{ category: string; commands: Command[] }[]> {
  const { registry, platform } = useShortcuts();
  const catalog = createMemo(() => {
    const visible = registry.commands().filter((c) => !c.disabledOn?.includes(platform.id));
    const grouped = new Map<string, Command[]>();
    for (const cmd of visible) {
      const list = grouped.get(cmd.category) ?? [];
      list.push(cmd);
      grouped.set(cmd.category, list);
    }
    return [...grouped.entries()].map(([category, commands]) => ({ category, commands }));
  });
  return catalog;
}
